"use client";

import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Video, Share2, Code, Zap } from "lucide-react";
import React, { useRef } from "react";

const services = [
  {
    title: "Content Creation",
    description: "From scripting to final video editing. We handle the entire creative process to deliver high-quality visual stories.",
    icon: <Video size={48} className="text-primary" />,
    features: ["Scripting", "Direction", "Cinematography", "Post-Production"],
    color: "from-primary/20",
  },
  {
    title: "Social Media Management",
    description: "Strategizing and managing your digital presence. We grow your brand through data-driven engagement and consistency.",
    icon: <Share2 size={48} className="text-secondary" />,
    features: ["Content Strategy", "Platform Growth", "Community Mgmt", "Analytics"],
    color: "from-secondary/20",
  },
  {
    title: "Web Development",
    description: "Building fast, secure, and modern websites. From portfolio sites to complex web applications.",
    icon: <Code size={48} className="text-accent" />,
    features: ["Custom Design", "SEO Optimized", "Responsive UI", "Secure CMS"],
    color: "from-accent/20",
  },
];

const TiltCard = ({ service, index }: { service: any; index: number }) => {
  const ref = useRef<HTMLDivElement>(null);
  
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const mouseXSpring = useSpring(x);
  const mouseYSpring = useSpring(y);

  const rotateX = useTransform(mouseYSpring, [-0.5, 0.5], ["10deg", "-10deg"]);
  const rotateY = useTransform(mouseXSpring, [-0.5, 0.5], ["-10deg", "10deg"]);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const xPct = mouseX / width - 0.5;
    const yPct = mouseY / height - 0.5;
    x.set(xPct);
    y.set(yPct);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{
        rotateY,
        rotateX,
        transformStyle: "preserve-3d",
      }}
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.7, delay: index * 0.2 }}
      className={`relative h-full glass-card p-8 group border border-white/5 bg-gradient-to-br ${service.color} to-transparent hover:border-white/20 transition-colors overflow-hidden`}
    >
      {/* Animated Glow on Hover */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-[radial-gradient(circle_at_var(--mouse-x)_var(--mouse-y),rgba(255,255,255,0.1)_0%,transparent_50%)]" />
      
      <div style={{ transform: "translateZ(50px)" }} className="relative z-10">
        <motion.div 
          className="mb-8 inline-block p-4 bg-surface/80 rounded-2xl border border-white/5 shadow-2xl"
          whileHover={{ scale: 1.1, rotate: 5 }}
        >
          {service.icon}
        </motion.div>
        <h3 className="text-3xl font-bold mb-4 tracking-tight">{service.title}</h3>
        <p className="text-text-muted mb-8 leading-relaxed text-lg">
          {service.description}
        </p>
        <ul className="flex flex-wrap gap-2">
          {service.features.map((feature: string, i: number) => (
            <li key={i} className="text-sm font-semibold px-4 py-2 bg-white/5 border border-white/10 rounded-full flex items-center gap-2 hover:bg-white/10 transition-colors">
              <Zap size={14} className="text-primary" />
              {feature}
            </li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
};

const Services = () => {
  return (
    <section id="services" className="py-32 relative">
      <div className="absolute top-0 w-full h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      
      <div className="container relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <span className="text-primary font-bold tracking-widest uppercase text-sm mb-4 block">What We Do</span>
          <h2 className="text-5xl md:text-7xl font-black mb-6">OUR EXPERTISE</h2>
          <p className="text-text-muted max-w-2xl mx-auto text-xl">
            End-to-end solutions for brands looking to make a massive impact in the digital landscape.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8" style={{ perspective: 1000 }}>
          {services.map((service, index) => (
            <TiltCard key={index} service={service} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
